import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sun, Leaf, Snowflake, Flower2 } from "lucide-react";
import { toast } from "sonner";

const SEASONS = [
  { key: "spring", label: "Spring", months: "Mar–May", icon: Flower2, tone: "bg-green-50 text-green-700" },
  { key: "summer", label: "Summer", months: "Jun–Aug", icon: Sun, tone: "bg-amber-50 text-amber-700" },
  { key: "fall", label: "Fall", months: "Sep–Nov", icon: Leaf, tone: "bg-orange-50 text-orange-700" },
  { key: "winter", label: "Winter", months: "Dec–Feb", icon: Snowflake, tone: "bg-blue-50 text-blue-700" },
];

const ALL_TYPES = ["SUV", "SUV 4x4", "Luxury", "Sedan", "Hatchback", "Coupe", "Convertible", "MPV / Family Vans", "Pickup Trucks", "Truck", "Van"];

function defaultsFor(types) {
  const m = {};
  types.forEach((t) => {
    m[t] = { spring: 1.0, summer: 1.0, fall: 1.0, winter: 1.0 };
  });
  return m;
}

export default function SeasonalPricing({ vendorCars = [] }) {
  // Show types this vendor actually owns; if none, show all so they can preconfigure.
  const types = vendorCars.length > 0
    ? Array.from(new Set(vendorCars.map((c) => c.car_type)))
    : ALL_TYPES;

  const [matrix, setMatrix] = useState(defaultsFor(types));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await api.get("/vendor/seasonal-pricing");
        if (cancelled) return;
        const next = defaultsFor(types);
        data.forEach((r) => {
          if (!next[r.car_type]) next[r.car_type] = { spring: 1, summer: 1, fall: 1, winter: 1 };
          next[r.car_type][r.season] = Number(r.multiplier) || 1;
        });
        setMatrix(next);
      } catch (e) {
        console.error("seasonal pricing load failed:", e);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [types.join("|")]); // eslint-disable-line react-hooks/exhaustive-deps

  const setCell = (carType, season) => (e) => {
    const v = parseFloat(e.target.value);
    setMatrix((m) => ({ ...m, [carType]: { ...m[carType], [season]: isNaN(v) ? 1 : v } }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const rules = [];
      Object.entries(matrix).forEach(([carType, byS]) => {
        SEASONS.forEach((s) => {
          rules.push({ car_type: carType, season: s.key, multiplier: Math.max(0.1, Math.min(10, Number(byS[s.key]) || 1)) });
        });
      });
      await api.put("/vendor/seasonal-pricing", { rules });
      toast.success("Seasonal pricing saved");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="seasonal-pricing-panel">
      <div className="rounded-2xl border border-border p-6 bg-white">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h3 className="font-display text-xl font-medium">Seasonal pricing</h3>
            <p className="text-sm text-neutral-500 mt-1 max-w-xl">
              Set a multiplier per car category for each season. <span className="font-medium text-neutral-700">1.0 = base price</span>, <span className="font-medium">1.4 = +40%</span>, <span className="font-medium">0.8 = −20%</span>. The multiplier is applied per rented day automatically at checkout.
            </p>
          </div>
          <Button onClick={save} disabled={saving} className="rounded-full bg-blue-600 hover:bg-blue-700" data-testid="save-seasonal-btn">
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </div>

        <div className="mt-6 grid grid-cols-4 gap-3">
          {SEASONS.map((s) => (
            <div key={s.key} className={`rounded-xl px-3 py-2 ${s.tone} flex items-center gap-2`}>
              <s.icon className="h-4 w-4" />
              <div>
                <div className="text-sm font-medium">{s.label}</div>
                <div className="text-[10px] uppercase tracking-wider opacity-70">{s.months}</div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-6 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-neutral-500">
                <th className="px-2 py-2 font-medium">Category</th>
                {SEASONS.map((s) => (<th key={s.key} className="px-2 py-2 font-medium">{s.label}</th>))}
              </tr>
            </thead>
            <tbody>
              {Object.keys(matrix).map((t) => (
                <tr key={t} className="border-t border-border" data-testid={`pricing-row-${t}`}>
                  <td className="px-2 py-3 font-medium">{t}</td>
                  {SEASONS.map((s) => (
                    <td key={s.key} className="px-2 py-2">
                      <Input
                        type="number"
                        step="0.05"
                        min="0.1"
                        max="10"
                        value={matrix[t][s.key]}
                        onChange={setCell(t, s.key)}
                        className="rounded-xl h-10 w-24"
                        data-testid={`pricing-${t}-${s.key}`}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {Object.keys(matrix).length === 0 && (
          <div className="text-sm text-neutral-500 mt-4">List a car first to configure category-specific pricing.</div>
        )}
      </div>
    </div>
  );
}
